import type { IBitcoinProvider as KeplrBitcoinProvider } from "@keplr-wallet/types";
import * as bitcoin from "bitcoinjs-lib";
import { BitcoinConnector } from "../../../../core/types/bitcoin";
import {
  BitcoinConnectorInvalidAccountError,
  BitcoinConnectorProviderNotFoundError,
  BitcoinConnectorTransactionFailedError,
} from "../errors";
import { broadcastTx } from "../helpers";

export class KeplrConnector implements BitcoinConnector {
  public get getProvider(): KeplrBitcoinProvider {
    const provider = (window as any)?.bitcoin_keplr;

    if (!provider) {
      throw new BitcoinConnectorProviderNotFoundError("Keplr");
    }

    return provider;
  }

  async sendBTC(to: string, amount: number) {
    const txHash = await this.getProvider.sendBitcoin(to, amount);

    if (!txHash) {
      throw new BitcoinConnectorTransactionFailedError();
    }

    return { txHash };
  }

  async requestAccount() {
    const accounts: string[] = await this.getProvider.requestAccounts();

    const account = accounts[0];

    if (typeof account !== "string") {
      throw new BitcoinConnectorInvalidAccountError();
    }

    return {
      address: account,
    };
  }

  async signPsbt(psbtHex: string): Promise<{ txHash: string }> {
    // Keplr follows the UniSat-style Bitcoin provider API: `signPsbt` signs and,
    // with `autoFinalized` (the default), finalizes every input, returning the
    // signed PSBT as a hex string.
    // https://docs.keplr.app/api/bitcoin
    const signedPsbtHex = await this.getProvider.signPsbt(psbtHex, {
      autoFinalized: true,
    });

    // Broadcast the finalized raw transaction directly rather than through
    // Keplr's `pushTx`, which proxies via Keplr's own Bitcoin indexer
    // (api-indexer-bitcoin.keplr.app) and fails when that service is down.
    const rawTxHex = bitcoin.Psbt.fromHex(signedPsbtHex)
      .extractTransaction()
      .toHex();

    const txHash = await broadcastTx(rawTxHex);

    if (!txHash) {
      throw new BitcoinConnectorTransactionFailedError();
    }

    return { txHash };
  }
}
